/* Shared presentational kit for the Order Details re-skin — mirrors the merchant panel's
   detail components (proship-web .../orders/detail): a card with a light header bar, and a
   label→value row. Presentational only; all data/handlers stay in OrderDetails.js. */
import React from 'react'
import { Box, Typography } from '@mui/material'

// Design tokens (admin theme palette values), matching the merchant panel's look.
export const T = {
  NAVY: '#212B36',
  SUBTLE: '#637381',
  BORDER: '#DFE3E8',
  LIGHT_BG: '#F9FAFB',
  WHITE: '#fff',
  PRIMARY: '#2065D1',
  SUCCESS: '#10B981', SUCCESS_BG: '#E8F5E9',
  ERROR: '#FF4842', ERROR_BG: '#FFEBEE',
  WARNING: '#B78103', WARNING_BG: '#FFF3E0',
  INFO: '#1890FF', INFO_BG: '#E3F2FD',
  FONT: 'Poppins, sans-serif',
}

// Rounded section card with a grey header bar + bold title and an optional right-side action.
export function SectionCard({ title, action, children, sx: sxOverride }) {
  return (
    <Box sx={{ borderRadius: '14px', border: `1px solid ${T.BORDER}`, bgcolor: T.WHITE, mb: 2, overflow: 'hidden', ...sxOverride }}>
      {title != null && (
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${T.BORDER}`, bgcolor: T.LIGHT_BG, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: T.FONT, fontSize: 13, fontWeight: 700, color: T.NAVY }}>{title}</Typography>
          {action}
        </Box>
      )}
      <Box sx={{ px: 2.5, py: 1.5 }}>{children}</Box>
    </Box>
  )
}

// Label (left, muted) → value (right, semibold navy) row with a hairline divider between rows.
export function InfoRow({ label, value, hide, tint }) {
  if (hide) return null
  const tinted = tint && TINTS[tint]
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.85, borderBottom: `1px solid ${T.BORDER}`, '&:last-child': { borderBottom: 'none' }, ...(tinted ? { bgcolor: tinted.bg, mx: -2.5, px: 2.5, borderLeft: `3px solid ${tinted.border}` } : {}) }}>
      <Typography sx={{ fontFamily: T.FONT, fontSize: 12, color: tinted ? tinted.labelColor : T.SUBTLE, fontWeight: 500, flexShrink: 0, mr: 2 }}>{label}</Typography>
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
        {typeof value === 'string' || typeof value === 'number' || value == null
          ? <Typography component='span' sx={{ fontFamily: T.FONT, fontSize: 12, color: tinted ? tinted.valueColor : T.NAVY, fontWeight: 600, wordBreak: 'break-word' }}>{value == null || value === '' ? '—' : value}</Typography>
          : value}
      </Box>
    </Box>
  )
}

// Highlight tints preserved from the admin page's <Field highlight=...> semantics.
const TINTS = {
  blue: { bg: '#EFF6FF', border: '#3B82F6', labelColor: '#1E40AF', valueColor: '#1E3A8A' },
  green: { bg: '#ECFDF5', border: '#10B981', labelColor: '#047857', valueColor: '#064E3B' },
  amber: { bg: '#FFFBEB', border: '#F59E0B', labelColor: '#B45309', valueColor: '#78350F' },
  red: { bg: '#FEF2F2', border: '#EF4444', labelColor: '#B91C1C', valueColor: '#7F1D1D' },
  purple: { bg: '#F5F3FF', border: '#8B5CF6', labelColor: '#6D28D9', valueColor: '#4C1D95' },
}
